import PropTypes from 'prop-types';

import type {Batch, StorableSummary} from 'constants/SampleConstants';
import type {BatchInfo, TargetEntry, TargetFile, TargetStorable} from 'folder-items/FileConstants';
import type {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import type {EvaluatedCellError, Selector} from 'notebook/tables/excess/EvaluationConstants';
import {externalCache} from 'notebook/tables/lookup-field/LookupFieldCacheConstants';
import type {ExternalCache} from 'notebook/tables/lookup-field/LookupFieldCacheConstants';
import type {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {TYPED_VALUE_SHAPE} from 'notebook/tables/typed-values/TypedValueConstants';
import {Optional} from 'util/Types';

import type {CustomEval, CustomEvalType} from './CustomEvalConstants';

// These properties are not tied to the value of the cell and are preserved when backspacing on a cell.
// Agent keep in sync: CELL_FORMAT_PROPERTIES in benchling/notebook/structured_tables/models/structured_table_row.py
export const CELL_FORMAT_PROPERTIES: Array<keyof Cell> = [
  'bold',
  'underline',
  'italic',
  'strikethrough',
  'selectorId',
  'precision',
  'align',
  'foregroundColor',
  'backgroundColor',
  'important',
  'scientificNotation',
  'mergeRowSpan',
  'mergeColSpan',
  'allowOverrideLockedCellBackgroundColor',
];

export const CELL_UI_PROPERTIES = [
  'formattedValue',
  'tdValue',
  'customEvalArgs',
  'customEval',
  'customEvalType',
  'customEvalOptions',
  'locked',
  'error',
  'userAnnotationIndicator',
  'grayOutContents',
  'exportModifierText',
  'allowOverrideLockedCellBackgroundColor',
] as const;

export type PastedCell = Cell & {
  selector?: Selector;
  parentStorableSummary?: StorableSummary;
  batch?: Batch;
  formula?: string;
};

export type FormulaAlignment = 'left' | 'right' | 'center';

/**
 * A table cell as the server sends it. `text` is null when the server stored a column default formula without
 * its text, which it does for rows it creates because it does not know their final position. Convert to a
 * `Cell` with `fillFormulaDefault`.
 */
export type FromServerCell = Omit<Cell, 'text'> & {text: string | null};

export interface Cell<CustomEvalOptions = unknown> {
  // If a cell has a typedValue, it should have an empty text string. (There may well
  // be old code that does not honor this. New code certainly should.)
  text: string;
  typedValue?: TypedValue | null;
  link?: Optional<string>;
  // this is set, on save, by client/notes/augmentNotePartTablesWithValues.js
  cachedTypedValue?: Optional<TypedValue>;
  cachedValue?: Optional<string>;
  bold?: Optional<boolean>;
  underline?: Optional<boolean>;
  italic?: Optional<boolean>;
  strikethrough?: Optional<boolean>;
  selectorId?: Optional<string>;
  precision?: Optional<number>;
  align?: Optional<FormulaAlignment>;
  foregroundColor?: Optional<string>;
  backgroundColor?: Optional<string>;
  grayOutContents?: Optional<boolean>;
  important?: Optional<boolean>;
  scientificNotation?: Optional<boolean>;
  // `mergeRowSpan` and `mergeColSpan` are the number of merged rows
  // and columns respectively.
  // Only the top left cell in a merged region should have these
  // properties set.
  mergeRowSpan?: number;
  mergeColSpan?: number;
  // Maps <schemaName> + <fieldName> -> cached results
  externalCache?: ExternalCache;
  itemLinks?: Array<BatchInfo> | Array<TargetFile> | Array<TargetEntry> | Array<TargetStorable> | null;
  schemaFieldSelectorOptionIds?: Array<string>;

  // Overrides for using a <TableEditable> for presentational purposes (should not be persisted):
  formattedValue?: Optional<JSX.Element>;
  tdValue?: Optional<JSX.Element>;
  // customEvalArgs is an array of formulas which will be passed into customEval, which is called in place
  // of normal ASTNode.eval via SpreadsheetEvaluator. This is a powerful feature for custom tables that
  // would like to construct a cell that is evaluated like an excess function would (has parameters that
  // can reference other cells), but does /not/ require there to be a user-facing excess function, as we
  // can use a custom JS function via customEval, e.g. RegistrationTableUtil.setFieldsForExistingBioentity
  customEvalArgs?: Optional<Array<string | null>>;
  customEval?: Optional<CustomEval<CustomEvalOptions>>;
  customEvalOptions?: CustomEvalOptions;
  customEvalType?: CustomEvalType;
  // Declare that this individual cell cannot be edited, and should have a gray background.
  // Currently only used on the frontend and will error if persisted to the server.
  locked?: Optional<boolean>;
  // Currently only used on the frontend
  error?: Optional<EvaluatedCellError>;
  userAnnotationIndicator?: Optional<JSX.Element>;
  exportModifierText?: string;
  // When true, an explicitly set `backgroundColor` overrides the default locked-cell gray background.
  allowOverrideLockedCellBackgroundColor?: Optional<boolean>;
}

export const cell = PropTypes.shape({
  text: PropTypes.string.isRequired,
  typedValue: TYPED_VALUE_SHAPE,
  link: PropTypes.string,
  // this is set, on save, by client/notes/augmentNotePartTablesWithValues.js
  typedCachedValue: TYPED_VALUE_SHAPE,
  cachedValue: PropTypes.string,
  bold: PropTypes.bool,
  underline: PropTypes.bool,
  italic: PropTypes.bool,
  strikethrough: PropTypes.bool,
  selectorId: PropTypes.string,
  precision: PropTypes.number,
  align: PropTypes.string,
  foregroundColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  grayOutContents: PropTypes.bool,
  important: PropTypes.bool,
  // `mergeRowSpan` and `mergeColSpan` are the number of merged rows
  // and columns respectively.
  // Only the top left cell in a merged region should have these
  // properties set.
  mergeRowSpan: PropTypes.number,
  mergeColSpan: PropTypes.number,
  // Maps <schemaName> + <fieldName> -> cached results
  externalCache,
  itemLinks: PropTypes.array,

  // Overrides for using a <TableEditable> for presentational purposes (should not be persisted):
  formattedValue: PropTypes.node,
  tdValue: PropTypes.node,
  // customEvalArgs is an array of formulas which will be passed into customEval, which is called in place
  // of normal ASTNode.eval via SpreadsheetEvaluator. This is a powerful feature for custom tables that
  // would like to construct a cell that is evaluated like an excess function would (has parameters that
  // can reference other cells), but does /not/ require there to be a user-facing excess function, as we
  // can use a custom JS function via customEval, e.g. RegistrationTableUtil.setFieldsForExistingBioentity
  customEvalArgs: PropTypes.arrayOf(PropTypes.string),
  customEval: PropTypes.func,
  customEvalType: PropTypes.string,
  // Declare that this individual cell cannot be edited, and should have a gray background.
  // Currently only used on the frontend and will error if persisted to the server.
  locked: PropTypes.bool,
  userAnnotationIndicator: PropTypes.node,
  allowOverrideLockedCellBackgroundColor: PropTypes.bool,
});

/**
 * Properties of Cell objects that should trigger evaluation if changed.
 *
 * Only includes properties that may be changed directly as a single operation.
 * (i.e. excludes merged range dims, custom eval fields, etc.)
 */
export const EVALUABLE_CELL_PROPERTIES: Array<string> = ['text', 'typedValue', 'selectorId'];

/**
 * A single cell property to set on every selected cell. A `null` or `undefined` value deletes the
 * property.
 */
export interface FormatPropertyChange {
  property: string;
  value?: unknown;
}

export const PLACEHOLDER_CELL: Cell = {text: '--', align: 'center'};

export interface MultiDocumentCellColors {
  [documentName: string]: CrossTableCellColors;
}
export interface CrossTableCellColors {
  [tableName: string]: CellColors;
}
/** Shades every cell within `box` with the `className` highlight class. */
export interface CellHighlight {
  box: MultiSelectionBox;
  className: string;
}
/**
 * A table's formula reference highlights, in formula order; where boxes overlap, the later entry
 * wins. Highlights are boxes rather than per-cell entries so their cost does not grow with the size
 * of a referenced range, which can extend far beyond the table (e.g. `A1:A10000000`).
 */
export type CellColors = ReadonlyArray<CellHighlight>;

// A partial DOMRect used to position the table cell editor.
export interface TableCellEditorPosition {
  top: number;
  left: number;
  minHeight: number;
  maxHeight: number;
  width: number;
}
